import type { Category } from "@/lib/category-slugs";
import type { SpotName, SpotTone } from "../Spot/SpotIcon";

/** A Resources article, as `<content folder>/<slug>` (e.g. `guides/decouvrir-les-taches`). */
export type ArticleRef = `${Category}/${string}`;

/** One step of the getting-started path. Copy lives in `lang/*` under `guide.step.<id>.*`. */
export type GuideStep = {
  /** Anchor id, identical in both languages. */
  id: string;
  /** Colour of the step (title number, how-to numbers, tip rule, demo ground, contents marker), distinct from its neighbours. */
  tone: SpotTone;
  /** Generated animation (`public/lottie/<scene>.<locale>.json`). */
  scene: string;
  /** Number of numbered how-to lines (`guide.step.<id>.how.1..n`). */
  how: number;
  /** Whether the step has a tip (`guide.step.<id>.tip`). */
  tip: boolean;
  /** Articles that cover the step; only the ones already published are shown. */
  articles: ArticleRef[];
  /** Optional action link under the how-to (`guide.step.<id>.cta`). */
  cta?: string;
};

/**
 * The getting-started path, in the order a new account needs it: capture, bring the
 * calendar, plan the day, then notes, projects, the team, and the import for people
 * coming from another tool. Every claim in the copy is grounded in the linked articles.
 */
export const GUIDE_STEPS: GuideStep[] = [
  {
    id: "premiere-tache",
    tone: "blue",
    scene: "tasks",
    how: 4,
    tip: true,
    articles: ["guides/debuter-avec-flots", "guides/decouvrir-les-taches", "guides/taches-recurrentes-routines"],
  },
  {
    id: "agenda",
    tone: "sky",
    scene: "calendar",
    how: 4,
    tip: false,
    articles: ["guides/decouvrir-l-agenda", "guides/plusieurs-agendas-une-vue", "guides/synchroniser-emploi-du-temps-ade"],
  },
  {
    id: "journee",
    tone: "amber",
    scene: "agenda",
    how: 4,
    tip: true,
    articles: ["actualites/vue-planning", "guides/decouvrir-l-assistant-cap"],
  },
  {
    id: "notes",
    tone: "purple",
    scene: "notes",
    how: 4,
    tip: true,
    articles: ["guides/decouvrir-les-notes", "actualites/notes-markdown-complet"],
  },
  {
    id: "projet",
    tone: "green",
    scene: "project",
    how: 4,
    tip: false,
    articles: ["guides/decouvrir-les-projets", "guides/decouvrir-les-documents"],
  },
  {
    id: "equipe",
    tone: "pink",
    scene: "invite",
    how: 4,
    tip: true,
    articles: ["guides/flots-entrepreneur-petite-equipe", "guides/collaborer-sur-une-note", "guides/agenda-partage-equipe"],
  },
  {
    id: "import",
    tone: "navy",
    scene: "import",
    how: 3,
    tip: true,
    articles: ["guides/migrer-notion-todoist-vers-flots"],
    cta: "https://dashboard.flots.app/import",
  },
];

/** One entry of the feature map; it links to the first of its articles that is published. */
export type GuideFeature = { id: string; spot: SpotName; articles: ArticleRef[] };

/** The feature map, grouped by what people want to do (not by app menu); `tone` colours its icons. */
export const GUIDE_FEATURE_GROUPS: { id: string; tone: SpotTone; features: GuideFeature[] }[] = [
  {
    id: "organize",
    tone: "blue",
    features: [
      { id: "tasks", spot: "tasks", articles: ["guides/decouvrir-les-taches"] },
      { id: "agenda", spot: "agenda", articles: ["actualites/vue-planning", "guides/decouvrir-l-agenda"] },
      { id: "cap", spot: "cap", articles: ["guides/decouvrir-l-assistant-cap"] },
    ],
  },
  {
    id: "write",
    tone: "purple",
    features: [
      { id: "notes", spot: "notes", articles: ["guides/decouvrir-les-notes"] },
      { id: "documents", spot: "documents", articles: ["guides/decouvrir-les-documents"] },
      { id: "whiteboards", spot: "whiteboards", articles: ["guides/decouvrir-les-tableaux-blancs"] },
    ],
  },
  {
    id: "team",
    tone: "green",
    features: [
      { id: "projects", spot: "projects", articles: ["guides/decouvrir-les-projets"] },
      { id: "live", spot: "live", articles: ["guides/collaborer-sur-une-note", "actualites/edition-en-direct-notes"] },
      { id: "workload", spot: "workload", articles: ["guides/agenda-partage-equipe"] },
    ],
  },
  {
    id: "connect",
    tone: "navy",
    features: [
      { id: "calendars", spot: "calendars", articles: ["guides/plusieurs-agendas-une-vue", "guides/decouvrir-l-agenda"] },
      { id: "import", spot: "import", articles: ["guides/migrer-notion-todoist-vers-flots"] },
      { id: "mcp", spot: "mcp", articles: ["guides/connecter-un-assistant-ia-mcp"] },
    ],
  },
];

/** Every article the guide may link to (deduplicated), for the server to resolve. */
export const GUIDE_ARTICLES: ArticleRef[] = Array.from(
  new Set([
    ...GUIDE_STEPS.flatMap((s) => s.articles),
    ...GUIDE_FEATURE_GROUPS.flatMap((g) => g.features.flatMap((f) => f.articles)),
  ]),
);

/** Published title and URL of an article, in the page's language. */
export type GuideArticleLink = { title: string; href: string };

/** Links the server resolved, keyed by `ArticleRef`; unpublished articles are absent. */
export type GuideArticleLinks = Record<string, GuideArticleLink>;
